import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { base_url } from '../api/api-url';

import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TablePagination, IconButton, TextField,
  Button, Typography, Box, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchBar from "../components/common/SearchBar";
import CustomTimeline from "../components/common/CustomTimeline";
import SnackNotification from "../components/common/SnackNotification";


const Department = () => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${base_url}/department`);
      const data = await res.json();
      setDepartments(data.data);
      setFilteredDepartments(data.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const onSubmit = async (data) => {
    if(data.label.length<10){
      setSnackbarMessage("Department name should contain atleast 10 character ");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return
    }
    if (!/^[A-Za-z\s]+$/.test(data.label)) {
      setSnackbarMessage("Department name should contain only alphabets");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      label: data.label.trim(),
      description: data.description.trim(),
      
    };

    try {
      let response;
      if (editingId) {
        response = await fetch(`${base_url}/department/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${base_url}/department`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error("Failed to save department");

      reset();
      setEditingId(null);
      setEditingIndex(null);
      setOpenDialog(false);
      fetchDepartments();
    } catch (error) {
      console.error("Submit Error:", error);
    }
  };

  const handleEdit = (index) => {
    const dept = filteredDepartments[index];
    setValue("label", dept.label);
    setValue("description", dept.description);
    setEditingId(dept._id);
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDelete = async (index) => {
    const dept = filteredDepartments[index];
    try {
      const response = await fetch(`${base_url}/department/${dept._id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete department");

      fetchDepartments();
      if (editingId === dept._id) {
        reset();
        setEditingId(null);
        setEditingIndex(null);
        setOpenDialog(false);
      }
    } catch (error) {
      console.error("Delete Error:", error);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleCloseDialog = () => {
    reset();
    setEditingId(null);
    setEditingIndex(null);
    setOpenDialog(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredDepartments].sort((a, b) => {
      const aVal = (a[key] || "").toLowerCase();
      const bVal = (b[key] || "").toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredDepartments(sorted);
    setSortConfig({ key, direction });
  };

  return (
    <Stack spacing={4}>
      {/* Dialog for Create / Edit */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#212529',
            color: '#fff',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #343a40', fontWeight: 'bold' }}>
          {editingId ? "Edit Department" : "Create Department"}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
            <Stack spacing={3} >
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Department Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("label", { required: "Department name is required" })}
                  error={!!errors.label}
                  helperText={errors.label?.message}
                  sx={{
                    bgcolor: '#343a40',
                    input: { color: '#fff' },
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6c757d" },
                      "&:hover fieldset": { borderColor: "#adb5bd" },
                      "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                    },
                    "& .MuiFormHelperText-root": { color: '#f44336' }
                  }}
                />
              </div>

              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                  {...register("description")}
                  sx={{
                    bgcolor: '#343a40',
                    textarea: { color: '#fff' },
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6c757d" },
                      "&:hover fieldset": { borderColor: "#adb5bd" },
                      "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                    },
                  }}
                />
              </div>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDialog}
              color="inherit"
              sx={{ color: '#adb5bd', '&:hover': { backgroundColor: 'rgba(173, 181, 189, 0.1)' } }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                textTransform: 'none',
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              {editingId ? "Update Department" : "Create Department"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
{/* <CustomTimeline/> */}
      <Paper
        elevation={3}
        sx={{
          bgcolor: '#1e1e1e',
          p: 2,
          borderRadius: 2,
          color: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={"wrap"}
          gap={2}
          mb={3}
        >
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Department List
          </Typography>

          <Box display="flex" gap={2} flexWrap={"wrap"}>
            <SearchBar
              data={departments}
              searchKeys={["label", "description"]}
              onSearch={(results) => setFilteredDepartments(results)}
            />  
         
<Button
  variant="contained"
  color="primary"
  size="small"
  startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
  onClick={() => {
    setEditingId(null);
    reset();
    setOpenDialog(true);
  }}
  sx={{
    textTransform: 'none',
    bgcolor: '#1976d2',
    '&:hover': { bgcolor: '#1565c0' },
    fontWeight: 'bold',
    padding: '4px 10px',      
    minHeight: '30px',        
    fontSize: '0.8rem',       
    lineHeight: 1.2
  }}
>
  Create Department
</Button>
          </Box>
        </Box>

        {filteredDepartments.length > 0 ? (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: "transparent", }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#8d8787' }}>
                    <TableCell sx={{ fontWeight: "bolder" }}>Sno</TableCell>
                    <TableCell
                      sx={{ fontWeight: "bolder", cursor: 'pointer' }}
                      onClick={() => handleSort("label")}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bolder", cursor: 'pointer' }}
                      onClick={() => handleSort("description")}
                    >
                      Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bolder" }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDepartments
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((dept, index) => (
                      <TableRow key={dept._id} hover>
                        <TableCell sx={{ color: "white", py: "5px" }}>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }}>{dept.label}</TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }}>{dept.description || "—"}</TableCell>
                        <TableCell sx={{ color: "white", py: "5px" }} align="center">
                          <IconButton sx={{ py: "5px" }} color="success" onClick={() => handleEdit(page * rowsPerPage + index)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton sx={{ py: "5px" }} onClick={() => handleDelete(page * rowsPerPage + index)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box  className="mui-wrapper mui-isolated-container p-0" sx={{ color: "white", px: 2, py: 1, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #ccc', flexWrap:"wrap" }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredDepartments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  background: "#8d8787",
                  borderRadius: "0px !important",
                  color: 'white !important',
                  flexWrap:"wrap",
                  '.MuiTablePagination-toolbar': { m: 0, color: 'white',overflow:{
                    xs:"scroll" ,
                    sm:"hidden" 
                  }
                
                },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: 'white !important',
                  }
                }}
              />
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 3 }}>
            No departments added yet.
          </Typography>
        )}
      </Paper>
          <SnackNotification
              open={snackbarOpen}
              message={snackbarMessage}
              severity={snackbarSeverity}
              handleClose={handleSnackbarClose}
            />
    </Stack>
  );
};

export default Department;
